import { Component, OnInit, Input } from "@angular/core";
import { Router } from "@angular/router";
import { Observable, Subscription } from "rxjs";

import { IconActionRendererComponent } from "../../../../common/components/ag-grid/icon-action-renderer.component" ;
import { CheckboxRendererComponent } from "../../../../common/components/ag-grid/checkbox-renderer.component";
import { AgGridBooleanFilterComponent } from "../../../../common/components/ag-grid/filters/boolean-filter.component";
import { DialogService } from "../../../../common/services/dialog.service";
import { RemindersService } from "../../../services/reminders.service";
import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { genId } from "../../../../core/services/genId";

import { Reminder, ReminderDeliveryMethod, ReminderType, ReminderTypeOptions } from "../../../models/reminders.model";
import { SubHeader } from "../../../../core/models/sub-header.model";
import { finalize, tap } from "rxjs/operators";
import { ColDef, GridOptions, GridApi } from "ag-grid-community"; 
import { HttpClient } from "@angular/common/http";
import { IReportContextValue } from "../../../../models/comodel/mergefieldsmodel.model";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";

@Component({
    selector: 'reminder-list',
    templateUrl: "./reminder-list.component.html",
    styleUrls: ["./reminder-list.component.scss"],
    standalone: false
})
export class ReminderListComponent implements OnInit {
  public clickedReminder: Reminder;
  public _subHeader: SubHeader = new SubHeader({ title: "Manage Reminders", buttons: [{ name: "Add New Reminder", method: () => this.add() }]});
  public reminders$: Observable<Reminder[]>;
  public reminders: Reminder[];
	public gridOptions: GridOptions;
	private gridApi?: GridApi;
  dataSubscriptions: Subscription = new Subscription();

    constructor(
        public http: HttpClient,
        public subHeaderService: SubHeaderService,
        public router: Router,
        private formatService: GridFormatterService,
        public svc: RemindersService,
        public dialogService: DialogService
	) { }

    ngOnInit() {
        this.subHeaderService.updateSubHeader(this._subHeader);
        let columns = this.createColumnDefs();
        this.gridOptions = this.formatService.BuildDefaultGridOptionsWithColumns(columns, false, true, true, true);
		this.gridOptions.onGridReady = (params) => {
			this.gridApi = params.api;
		};
        if (this.gridApi) {
			this.gridApi.setGridOption("loading", true);
        }

        this.dataSubscriptions.add(this.svc.getAll().pipe(
            tap((data) => {
                this.reminders = data;
            })).subscribe({
                next: (success) => {
                    if (this.gridApi != null) {
                        this.gridApi.setGridOption("rowData", this.reminders);
				        this.gridApi.setGridOption("loading", false);
				        this.gridApi.sizeColumnsToFit();
                    }
            }, error: (fail) => {
                console.error(fail);
                if (this.gridApi != null) {
                    this.gridApi.setGridOption("loading", false);
                }
        }}
    ));
    }

    ngOnDestroy() {
        this.dataSubscriptions.unsubscribe();
    }

    public getDisplayName(method: ReminderDeliveryMethod): string {
        if (method === ReminderDeliveryMethod.Email)
          return "Email";
        if (method === ReminderDeliveryMethod.RegularMail)
          return "Regular Mail";
        else
          return "Certified Mail";
    }

    public createColumnDefs(): ColDef[] {
        const nameColumn = {
          headerName: "Reminder Name",
          field: "name",
          minWidth: 100
        };
        const activeColumn = {
          headerName: "Active",
          field: "active",
          maxWidth: 150,
          minWidth: 100,
          filter: AgGridBooleanFilterComponent,
          filterParams: {
            fieldPath: '$.active',
            isInServerSideFilterMode: false
          },
          tooltipValueGetter: this.formatService.formatBooleanToYesNo("active", "N/A"),
          cellRenderer: CheckboxRendererComponent,
          cellRendererParams: {
            action: () => { return false; },
            field: "active",
            description: "Active",
            isVisible: () => { return true; },
            isEnabled: () => { return false; },
            renderAsYesNo: true
          },
        };
        const actionColumn = {
          headerName: "",
          field: "",
          maxWidth: 100,
          minWidth: 100,
          cellStyle: { 'text-align': 'right', 'line-height': 1.75, 'padding-right': '1.5rem' },
          cellRenderer: IconActionRendererComponent,
          cellRendererParams: {
            buttons: [
              {
                action: (obj: Reminder) => this.edit(obj),
                label: 'Edit Reminder',
                icon: 'edit'
              }, {
                action: (obj: Reminder) => this.copy(obj),
                label: 'Copy Reminder',
                icon: 'copy'
               },
               {
                action: (obj: Reminder) => this.delete(obj),
                label: 'Delete Reminder',
                icon: 'delete'
              }
            ]
          }
        };
        const timeframeColumn = {
          headerName: "Timeframe in Days",
          field: "timeframe",
          maxWidth: 150,
          minWidth: 100
        };
        const typeColumn = {
          headerName: "Reminder Type",
          field: "type",
          valueGetter: (params: any) => {
            return (ReminderTypeOptions as any)[ReminderType[params.data.type]].text;
          },
          maxWidth: 300,
          minWidth: 250
        };

        const columns: ColDef[] = [];
        columns.push(nameColumn);
        columns.push(activeColumn);
        columns.push(timeframeColumn);
        columns.push(typeColumn);
        columns.push(actionColumn);
        return columns;
    }

    public copy(n: Reminder): void {
        console.log("copy");
        let toSave = JSON.parse(JSON.stringify(n));
        toSave.id = genId();
        toSave.active = false;

        var saveSubscription$ = this.svc.post(toSave)
            .pipe(
                finalize(() => {
                    this.dataSubscriptions.add(saveSubscription$);
                })
            ).subscribe({
                next: (data) => { this.router.navigate(["/admin/reminders", toSave.id]); },
                error: (error) => { console.log(error); alert("ERROR SAVING REMINDER"); }
            });
    }

    public delete(n: Reminder): void {
        this.clickedReminder = n;
        console.log("delete");
        this.dialogService.openDialog('confirmCancel', `Are you sure you want to delete ${n.name}`);

        console.log(`deleting from service ${n.id}`);
    }

    public editTest(): void {
        console.log("test");
    }

    public edit(n: Reminder): void {
        console.log('edit');
        this.router.navigate(["/admin/editReminder", n.id]);
    }

    public add(): void {
        console.log("add");
        this.router.navigate(["/admin/editReminder", 'new']);
    }

    public onDialogConfirm(): void {
        this.dialogService.closeDialog();

        var deleteSubscription$ = this.svc.delete(this.clickedReminder.id)
            .pipe(
                finalize(() => {
                    this.dataSubscriptions.add(deleteSubscription$);
                })
            ).subscribe({
                next: (data) => { this.reminders$ = this.svc.getAll() },
                error: (error) => { console.log(error); alert("ERROR DELETING REMINDER"); }
            });
    }

    public onDialogClose(): void {
        this.dialogService.closeDialog();
    }

    public generateAllActiveReminders(): void {
        console.log("Making call to generate reminders");

        var generateAllActiveRemindersSubscription$ = this.svc.generateAllActiveReminders()
            .pipe(
                finalize(() => {
                    this.dataSubscriptions.add(generateAllActiveRemindersSubscription$);
                })
        ).subscribe({
                next: (data) => { console.log("Successfully made call to generate reminders"); },
                error: (error) => { console.log(error); alert("ERROR GENERATING REMINDER"); }
        });
    }

    public getMergeFieldsByReminderType(reminderType: ReminderType): Observable<Array<IReportContextValue>> {
        return this.http.get<IReportContextValue[]>(`/api/reminders/fetchReminderMergeFields/${reminderType}`);
    }
}
